import { useState, useCallback, useRef, useEffect } from 'react';

// Type definitions for the Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionInterface extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  onspeechstart: (() => void) | null;
  onspeechend: (() => void) | null;
  onaudiostart: (() => void) | null;
  onaudioend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInterface;
}

// Browser detection helpers
const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isSafari = (): boolean => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

const isChrome = (): boolean => {
  return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
};

const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

export type SpeechRecognitionError = 
  | 'not-supported'
  | 'no-speech'
  | 'audio-capture'
  | 'network'
  | 'not-allowed'
  | 'aborted'
  | 'service-not-allowed'
  | 'bad-grammar'
  | 'language-not-supported'
  | 'siri-disabled'
  | 'unknown';

export interface UseSpeechToTextOptions {
  language?: string;
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  onError?: (error: SpeechRecognitionError, message: string) => void;
  onListeningChange?: (isListening: boolean) => void;
}

export interface UseSpeechToTextReturn {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  interimTranscript: string;
  error: SpeechRecognitionError | null;
  errorMessage: string | null;
  startListening: () => Promise<void>;
  stopListening: () => void;
  resetTranscript: () => void;
  browserInfo: {
    isMobile: boolean;
    isIOS: boolean;
    isSafari: boolean;
    isChrome: boolean;
    isAndroid: boolean;
    requiresSiri: boolean;
    supportMessage: string | null;
  };
}

// Get the SpeechRecognition constructor with proper typing
const getSpeechRecognition = (): SpeechRecognitionConstructor | null => {
  if (typeof window === 'undefined') return null;
  
  const win = window as Window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
    mozSpeechRecognition?: SpeechRecognitionConstructor;
    msSpeechRecognition?: SpeechRecognitionConstructor;
  };
  
  return (
    win.SpeechRecognition ||
    win.webkitSpeechRecognition ||
    win.mozSpeechRecognition ||
    win.msSpeechRecognition ||
    null
  );
};

const getErrorMessage = (error: SpeechRecognitionError): string => {
  switch (error) {
    case 'not-supported':
      return 'Speech recognition is not supported in this browser. Please use Chrome or Safari.';
    case 'no-speech':
      return 'No speech was detected. Please try again.';
    case 'audio-capture':
      return 'No microphone was found or microphone access was blocked.';
    case 'network':
      return 'Network error occurred. Please check your connection.';
    case 'not-allowed':
      return 'Microphone permission was denied. Please allow microphone access to use speech input.';
    case 'aborted':
      return 'Speech recognition was aborted.';
    case 'service-not-allowed':
      return 'Speech recognition service is not allowed.';
    case 'siri-disabled':
      return 'On iOS/Safari, Siri must be enabled for speech recognition to work. Enable Siri in Settings.';
    case 'language-not-supported':
      return 'The selected language is not supported.';
    default:
      return 'An error occurred with speech recognition. Please try again.';
  }
};

const getSupportMessage = (): string | null => {
  const ios = isIOS();
  const safari = isSafari();
  const mobile = isMobile();
  
  if (ios || safari) {
    return 'Speech recognition on Safari requires Siri to be enabled. If speech input is not working, please enable Siri in your device settings.';
  }
  
  if (mobile && !isChrome()) {
    return 'For best results, use Chrome on mobile devices.';
  }
  
  return null;
};

// Request microphone permission explicitly
const requestMicrophonePermission = async (): Promise<boolean> => {
  try {
    // Check if we already have permission
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (result.state === 'granted') {
          return true;
        }
      } catch {
        // permissions.query might not support microphone in all browsers
      }
    }
    
    // Request permission by getting user media
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Immediately stop the stream - we just needed the permission
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (err) {
    console.warn('Microphone permission denied:', err);
    return false;
  }
};

export const useSpeechToText = (options: UseSpeechToTextOptions = {}): UseSpeechToTextReturn => {
  const { language = 'en-US', onTranscript, onError, onListeningChange } = options;
  
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<SpeechRecognitionError | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const recognitionRef = useRef<SpeechRecognitionInterface | null>(null);
  const isListeningRef = useRef(false);
  const shouldBeListeningRef = useRef(false);
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  
  const SpeechRecognitionClass = getSpeechRecognition();
  const isSupported = SpeechRecognitionClass !== null;
  
  const browserInfo = {
    isMobile: isMobile(),
    isIOS: isIOS(),
    isSafari: isSafari(),
    isChrome: isChrome(),
    isAndroid: isAndroid(),
    requiresSiri: isIOS() || isSafari(),
    supportMessage: getSupportMessage(),
  };

  // Update listening state and notify
  const updateListeningState = useCallback((listening: boolean) => {
    setIsListening(listening);
    isListeningRef.current = listening;
    onListeningChange?.(listening);
  }, [onListeningChange]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onstart = null;
        recognitionRef.current.onaudiostart = null;
        recognitionRef.current.abort();
      } catch (e) {
        // Ignore cleanup errors
      }
      recognitionRef.current = null;
    }
  }, []);

  // Start a fresh recognition instance
  const startRecognitionInstance = useCallback(() => {
    if (!SpeechRecognitionClass || !shouldBeListeningRef.current) return;
    
    // Clean up existing instance
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onstart = null;
        recognitionRef.current.abort();
      } catch (e) {
        // Ignore
      }
    }
    
    const recognition = new SpeechRecognitionClass();
    recognitionRef.current = recognition;
    
    // Configure recognition
    recognition.lang = language;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    
    // Use continuous mode on desktop Chrome/Edge for better results
    // Disable on iOS/Safari/Android where it's problematic
    recognition.continuous = !browserInfo.isIOS && !browserInfo.isSafari && !browserInfo.isAndroid;
    
    recognition.onaudiostart = () => {
      // Audio capture has started - this means the mic is working
      setError(null);
      setErrorMessage(null);
    };
    
    recognition.onstart = () => {
      retryCountRef.current = 0; // Reset retry count on successful start
      updateListeningState(true);
      setError(null);
      setErrorMessage(null);
    };
    
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let currentInterim = '';
      
      // Process all results from the beginning to handle edge cases
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          currentInterim += result[0].transcript;
        }
      }
      
      if (finalTranscript) {
        setTranscript(finalTranscript.trim());
        onTranscript?.(finalTranscript.trim(), true);
        setInterimTranscript('');
      }
      
      if (currentInterim) {
        setInterimTranscript(currentInterim);
        onTranscript?.(currentInterim, false);
      }
    };
    
    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.warn('Speech recognition error:', event.error);
      
      // Handle specific errors
      switch (event.error) {
        case 'no-speech':
          // No speech detected - this is normal, just keep listening if we should
          if (shouldBeListeningRef.current && !browserInfo.isIOS && !browserInfo.isSafari) {
            // Don't treat as error, let onend handle restart
            return;
          }
          break;
          
        case 'aborted':
          // Intentionally aborted - not an error
          if (!shouldBeListeningRef.current) {
            return;
          }
          break;
          
        case 'not-allowed':
        case 'service-not-allowed':
          const errorType = browserInfo.requiresSiri ? 'siri-disabled' : 'not-allowed';
          const message = getErrorMessage(errorType);
          setError(errorType);
          setErrorMessage(message);
          onError?.(errorType, message);
          shouldBeListeningRef.current = false;
          updateListeningState(false);
          return;
          
        case 'audio-capture':
          setError('audio-capture');
          setErrorMessage(getErrorMessage('audio-capture'));
          onError?.('audio-capture', getErrorMessage('audio-capture'));
          shouldBeListeningRef.current = false;
          updateListeningState(false);
          return;
          
        case 'network':
          // Network error - might be temporary, allow retry
          if (retryCountRef.current < maxRetries && shouldBeListeningRef.current) {
            retryCountRef.current++;
            return; // Let onend handle restart
          }
          setError('network');
          setErrorMessage(getErrorMessage('network'));
          onError?.('network', getErrorMessage('network'));
          shouldBeListeningRef.current = false;
          updateListeningState(false);
          return;
      }
    };
    
    recognition.onend = () => {
      // Recognition ended - restart if we should still be listening
      if (shouldBeListeningRef.current) {
        // Small delay before restarting to avoid rapid cycling
        restartTimeoutRef.current = setTimeout(() => {
          if (shouldBeListeningRef.current) {
            startRecognitionInstance();
          }
        }, browserInfo.isIOS || browserInfo.isSafari ? 200 : 50);
      } else {
        updateListeningState(false);
      }
    };
    
    try {
      recognition.start();
      
      // Set a timeout to detect if start failed silently
      startTimeoutRef.current = setTimeout(() => {
        if (shouldBeListeningRef.current && !isListeningRef.current) {
          // Start seems to have failed silently - retry
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current++;
            startRecognitionInstance();
          } else {
            const message = getErrorMessage('unknown');
            setError('unknown');
            setErrorMessage(message);
            onError?.('unknown', message);
            shouldBeListeningRef.current = false;
          }
        }
      }, 2000);
      
    } catch (e) {
      const err = e as Error;
      console.warn('Failed to start speech recognition:', err);
      
      // If already started, that's fine
      if (err.message?.includes('already started')) {
        return;
      }
      
      // Retry on other errors
      if (retryCountRef.current < maxRetries && shouldBeListeningRef.current) {
        retryCountRef.current++;
        restartTimeoutRef.current = setTimeout(() => {
          if (shouldBeListeningRef.current) {
            startRecognitionInstance();
          }
        }, 300);
        return;
      }
      
      const message = getErrorMessage('unknown');
      setError('unknown');
      setErrorMessage(message);
      onError?.('unknown', message);
      shouldBeListeningRef.current = false;
      updateListeningState(false);
    }
  }, [SpeechRecognitionClass, language, browserInfo.isIOS, browserInfo.isSafari, browserInfo.isAndroid, browserInfo.requiresSiri, onTranscript, onError, updateListeningState]);

  const startListening = useCallback(async () => {
    if (!isSupported) {
      const message = getErrorMessage('not-supported');
      setError('not-supported');
      setErrorMessage(message);
      onError?.('not-supported', message);
      return;
    }
    
    // Request microphone permission first (especially important on mobile)
    const hasPermission = await requestMicrophonePermission();
    if (!hasPermission) {
      const errorType = browserInfo.requiresSiri ? 'siri-disabled' : 'not-allowed';
      const message = getErrorMessage(errorType);
      setError(errorType);
      setErrorMessage(message);
      onError?.(errorType, message);
      return;
    }
    
    // Clean up any existing recognition
    cleanup();
    
    // Mark that we want to be listening
    shouldBeListeningRef.current = true;
    retryCountRef.current = 0;
    
    // Start recognition
    startRecognitionInstance();
  }, [isSupported, browserInfo.requiresSiri, cleanup, startRecognitionInstance, onError]);

  const stopListening = useCallback(() => {
    shouldBeListeningRef.current = false;
    
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore stop errors
      }
    }
    
    updateListeningState(false);
  }, [updateListeningState]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldBeListeningRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  return {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    error,
    errorMessage,
    startListening,
    stopListening,
    resetTranscript,
    browserInfo,
  };
};
